import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import FormikForm from '../../form/FormikForm';
import BskSatirlari from './BskSatirlari';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  Card,
  Button,
  Typography,
  Space,
  Spin,
  Table,
  Modal,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Row,
  Col,
  Popconfirm,
  Alert,
  message,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { API_BASE_URL as SHARED_API_BASE_URL } from '../../../utils/config';
import { formatMoney, inputNumberMoneyProps } from '../../../utils/currency';
import { trIncludes } from '../../../utils/trText';

const { Title, Text } = Typography;
const { Option } = Select;

// 26.10 düzeltmesi (F4.1/F4.2): mevcut bir banka sözleşmesinin düzenlenmesi güncel
// 'sozlesme' kaydına (form 231, id ile Update) yönlenir. Eskiden state yoksa formData null
// kalıyor ve sayfa sonsuza dek 'Yükleniyor...' gösteriyordu; eski 'sozlesmeler' formu (332)
// ile gelen düzenleme istekleri (uniqueValue dolu) de bu yola alınır. 332 + Insert yalnız
// 'Banka Sözleşme Ekle' akışında kalır.
const guncelBankaFormu = (id) => ({
  formId: 231,
  title: 'Banka Sözleşmesi',
  tableName: 'sozlesme',
  uniqueField: 'id',
  uniqueValue: String(id),
  extraData: { _override_function: 'Update' },
  guncelTablo: true,
});

function BankaSozlesmeFormDetay() {
  const location = useLocation();
  const { id: urlId } = useParams();
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const API_BASE_URL = SHARED_API_BASE_URL;
  const [formData, setFormData] = useState(null);
  const [kabinler, setKabinler] = useState([]);
  const [kabinlerLoading, setKabinlerLoading] = useState(false);
  const [lokasyonModalVisible, setLokasyonModalVisible] = useState(false);
  const [lokasyonListesi, setLokasyonListesi] = useState([]);
  const [lokasyonLoading, setLokasyonLoading] = useState(false);
  const [selectedLokasyon, setSelectedLokasyon] = useState(null);
  const [selectedKabin, setSelectedKabin] = useState(null);
  const [kabinListesi, setKabinListesi] = useState([]);
  const [kabinLoading, setKabinLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [kiraBedeli, setKiraBedeli] = useState(0);
  const [kabinBedeli, setKabinBedeli] = useState(0);
  const [elektrikBedeli, setElektrikBedeli] = useState(0);
  const [ortakAlanBedeli, setOrtakAlanBedeli] = useState(0);
  const [kiraBaslangic, setKiraBaslangic] = useState(null);
  const [eklemeLoading, setEklemeLoading] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [currentFormId, setCurrentFormId] = useState(null);
  const [modalTitle, setModalTitle] = useState('Form');
  const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
  const [currentUniqueField, setCurrentUniqueField] = useState('');
  const [currentTableName, setCurrentTableName] = useState('');
  const [formExtraData, setFormExtraData] = useState({});
  
  const [bankaBaslik, setBankaBaslik] = useState(null);
  const [bankaSatirlari, setBankaSatirlari] = useState([]);
  const [bankaSatirYukleniyor, setBankaSatirYukleniyor] = useState(false);
  const [bankaBulunamadi, setBankaBulunamadi] = useState(false);
  const [bankaBaslikHata, setBankaBaslikHata] = useState(false);

  useEffect(() => {
    const st = location.state;
    const duzenlemeIstegi = st?.formId && st.tableName === 'sozlesmeler' && st.uniqueValue;
    const kayitId = (duzenlemeIstegi ? st.uniqueValue : null) ?? st?.record?.id ?? st?.record?.ID ?? urlId;
    if (st?.formId && !duzenlemeIstegi) {
      setFormData(st);
    } else if (kayitId && /^\d+$/.test(String(kayitId))) {
      setFormData(guncelBankaFormu(kayitId));
    }
  }, [location.state, urlId]);

  useEffect(() => {
    if (formData?.guncelTablo) {
      // başka bir sözleşmeye geçildiyse önceki başlık (ekleyen) yeni forma taşınmasın
      setBankaBaslik(null);
      setBankaBaslikHata(false);
      fetchBankaSatirlari();
    } else if (formData?.uniqueValue) {
      fetchKabinler();
    }
  }, [formData?.uniqueValue, formData?.guncelTablo]);

  // Güncel tabloda: sözleşme başlığı + bağlı banka_sozlesme_kayit satırları (salt-okur)
  const fetchBankaSatirlari = async () => {
    if (!formData?.uniqueValue) return;
    try {
      setBankaSatirYukleniyor(true);
      const res = await axios.get(`${API_BASE_URL}/form/lokasyon-listesi`, {
        params: { sozlesme: formData.uniqueValue, tip: 'banka' }
      });
      setBankaBaslik(res.data?.sozlesme || null);
      setBankaBulunamadi(!res.data?.sozlesme);
      setBankaBaslikHata(false);
      setBankaSatirlari(Array.isArray(res.data?.satirlar) ? res.data.satirlar : []);
    } catch (err) {
      console.error('Banka sözleşme kabinleri yüklenemedi:', err);
      setBankaBaslikHata(true);
      message.error(err?.response?.data?.detail || 'Sözleşmeye bağlı kabinler yüklenemedi');
    } finally {
      setBankaSatirYukleniyor(false);
    }
  };

  useEffect(() => {
    const lokasyonId = selectedLokasyon?.maptriksID;
    if (lokasyonId) {
      fetchKabinListesi(lokasyonId);
    } else {
      setKabinListesi([]);
      setSelectedKabin(null);
    }
  }, [selectedLokasyon]);

  const fetchKabinler = async () => {
    if (!formData?.uniqueValue) return;
    
    try {
      setKabinlerLoading(true);
      const response = await axios.post(`${API_BASE_URL}/list/execute-query/banka_sozlesme_kabinler`, {
        params: {
          sozlesmeId: formData.uniqueValue
        }
      }, {
        headers: {
          ...(localStorage.getItem('auth_token') && {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          })
        }
      });
      const rawData = response.data?.data || response.data || [];
      const uniqueKabinler = [];
      const seenLokasyonNos = new Set();
      rawData.forEach(kabin => {
        const lokasyonNo = kabin.Lokasyon || kabin.lokasyon || kabin.maptriksID || kabin.MaptriksID;
        if (lokasyonNo && !seenLokasyonNos.has(lokasyonNo)) {
          seenLokasyonNos.add(lokasyonNo);
          uniqueKabinler.push(kabin);
        }
      });
      setKabinler(response.data?.data || response.data || []);
    } catch (err) {
      console.error('Kabinler yüklenirken hata:', err);
      message.error('Kabinler yüklenirken hata oluştu');
    } finally {
      setKabinlerLoading(false);
    }
  };

  const fetchAllLokasyonlar = async () => {
    try {
      setLokasyonLoading(true);
      const response = await axios.post(`${API_BASE_URL}/list/execute-query/sozlesme_lokasyonlar`, {
        params: {}
      }, {
        headers: {
          ...(localStorage.getItem('auth_token') && {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          })
        }
      });
      setLokasyonListesi(response.data);
    } catch (err) {
      message.error('Lokasyon listesi yüklenirken hata oluştu');
    } finally {
      setLokasyonLoading(false);
    }
  };

  const fetchKabinListesi = async (maptriksId) => {
    try {
      setKabinLoading(true);
      
      const [doluKabinlerRes, kabinBilgileriRes] = await Promise.all([
        axios.post(`${API_BASE_URL}/list/execute-query/lokasyon_dolu_kabinler`, {
          params: {
            lokasyonId: maptriksId
          }
        }, {
          headers: {
            ...(localStorage.getItem('auth_token') && {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            })
          }
        }),
        axios.post(`${API_BASE_URL}/list/execute-query/lokasyon_kabin_bilgileri`, {
          params: {
            lokasyonId: maptriksId
          }
        }, {
          headers: {
            ...(localStorage.getItem('auth_token') && {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            })
          }
        })
      ]);

      const doluKabinler = doluKabinlerRes.data || [];
      const kabinBilgileri = kabinBilgileriRes.data?.[0] || {};
      const kabinDoluluk = {};
      doluKabinler.forEach(sozlesme => {
        kabinDoluluk[sozlesme.kabin] = sozlesme;
      });
      
      const toplamKabin = parseInt(kabinBilgileri.toplamKabin) || 0;
      let kurulacakKabin = parseInt(kabinBilgileri.kurulacakKabin) || 4;
      
      if (toplamKabin > kurulacakKabin) {
        kurulacakKabin = toplamKabin;
      }
      
      if (!kabinBilgileri.kurulacakKabin && toplamKabin === 0) {
        kurulacakKabin = 4;
      }
      
      const kabinOptions = [];
      
      if (toplamKabin > 0) {
        kabinOptions.push({
          value: null,
          label: 'Kabin Seçiniz',
          disabled: true
        });
      }
      
      for (let i = 1; i <= kurulacakKabin; i++) {
        if (kabinDoluluk[i]) {
          const bankaAdi = kabinDoluluk[i].bankaAdi || 'Bilinmiyor';
          kabinOptions.push({
            value: i,
            label: `${i} - Dolu (${bankaAdi})`,
            disabled: false
          });
        } else {
          if (toplamKabin >= i) {
            kabinOptions.push({
              value: i,
              label: `${i} - Boş`,
              disabled: false
            });
          } else {
            kabinOptions.push({
              value: i,
              label: `${i} - Kurulu Değil`,
              disabled: false
            });
          }
        }
      }
      
      if (toplamKabin === 0 && kabinOptions.length === 0) {
        for (let i = 1; i <= kurulacakKabin; i++) {
          if (kabinDoluluk[i]) {
            const bankaAdi = kabinDoluluk[i].bankaAdi || 'Bilinmiyor';
            kabinOptions.push({
              value: i,
              label: `${i} - Kurulu Kabin Yok (${bankaAdi})`,
              disabled: false
            });
          } else {
            kabinOptions.push({
              value: i,
              label: `${i} - Kurulu Kabin Yok`,
              disabled: false
            });
          }
        }
      }
      
      setKabinListesi(kabinOptions);
    } catch (err) {
      console.error('Kabin listesi yüklenirken hata:', err);
      message.warning('Kabin listesi yüklenirken hata oluştu');
      setKabinListesi([]);
    } finally {
      setKabinLoading(false);
    }
  };

  const handleKabinEkle = async () => {
    if (!selectedLokasyon || !selectedKabin) {
      message.warning('Lütfen lokasyon ve kabin seçin');
      return;
    }

    try {
      setEklemeLoading(true);     
      const payload = {
        sozlesme: formData.uniqueValue,
        lokasyon: selectedLokasyon?.MaptriksID || selectedLokasyon?.maptriksID,
        kabin: selectedKabin,
        kiraBedeli: parseFloat(kiraBedeli) || 0,
        kabinBedeli: parseFloat(kabinBedeli) || 0,
        elektrikBedeli: parseFloat(elektrikBedeli) || 0,
        ortak_alan_gideri: parseFloat(ortakAlanBedeli) || 0,
        kiraBaslangic: kiraBaslangic ? kiraBaslangic.format('YYYY-MM-DD') : null,
        active: 1,
      };

      await axios.post(`${API_BASE_URL}/form/submit-form`, {
        form_id: 0,
        table_name: 'sozlesmeler',
        function: 'Insert',
        form_data: payload
      }, {
        headers: {
          ...(localStorage.getItem('auth_token') && {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          })
        }
      });
      
      message.success('Kabin başarıyla eklendi');
      setLokasyonModalVisible(false);
      resetForm();
      fetchKabinler();
    } catch (err) {
      console.error('Kabin eklenirken hata:', err);
      message.error(err?.response?.data?.detail || 'Kabin eklenirken hata oluştu');
    } finally {
      setEklemeLoading(false);
    }
  };

  const openFormModal = (formId, title = 'Form', tableName = '', uniqueField = '', uniqueValue = null, extraData = {}, overrideFunction = null) => {
    setModalTitle(title);
    setCurrentFormId(formId);
    setCurrentTableName(tableName);
    setCurrentUniqueField(uniqueField);
    setCurrentUniqueValue(uniqueValue);
    setFormExtraData({
      ...extraData,
      ...(overrideFunction && { _override_function: overrideFunction })
    });
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setCurrentFormId(null);
    setModalTitle('Form');
    setCurrentUniqueValue(null);
    setCurrentUniqueField('');
    setCurrentTableName('');
    setFormExtraData({});
  };

  const handleFormModalSuccess = (result) => {
    closeFormModal();
    message.success('Kabin başarıyla güncellendi');
    fetchKabinler();
  };

  const handleKabinDuzenle = async (sozlesme) => {
    openFormModal(
      334,
      'Kabin Düzenle',
      'sozlesmeler',
      'sozlesme',
      sozlesme,
      {},
      'Update'
    );
  };

  const handleKabinSil = async (sozlesme) => {
    try {
      await axios.post(`${API_BASE_URL}/form/submit-form`, {
        form_id: 0,
        table_name: 'sozlesmeler',
        function: 'Update',
        unique_field: 'id',
        unique_value: sozlesme,
        form_data: {
          active: 0
        }
      }, {
        headers: {
          ...(localStorage.getItem('auth_token') && {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          })
        }
      });
      
      message.success('Kabin başarıyla silindi');
      fetchKabinler();
    } catch (err) {
      console.error('Kabin silinirken hata:', err);
      message.error('Kabin silinirken hata oluştu');
    }
  };

  const resetForm = () => {
    setSelectedLokasyon(null);
    setSelectedKabin(null);
    setKiraBedeli(0);
    setKabinBedeli(0);
    setElektrikBedeli(0);
    setOrtakAlanBedeli(0);
    setKiraBaslangic(null);
  };

  const handleLokasyonModalAc = () => {
    setSearchText('');
    setLokasyonModalVisible(true);
    fetchAllLokasyonlar();
  };

  const filteredLokasyonListesi = lokasyonListesi.filter(lokasyon => {
    if (!searchText) return true;
    return (
      trIncludes(lokasyon.maptriksID?.toString(), searchText) ||
      trIncludes(lokasyon['Lokasyon Adı'], searchText) ||
      trIncludes(lokasyon.Durum, searchText) ||
      trIncludes(lokasyon.Kategori, searchText) ||
      trIncludes(lokasyon.İl, searchText) ||
      trIncludes(lokasyon.İlçe, searchText)
    );
  });

  const kabinKolonlari = [
    {
      title: 'Lokasyon No',
      dataIndex: 'lokasyonNo',
      key: 'lokasyonNo',
      width: 90,
    },
    {
      title: 'Lokasyon Adı',
      dataIndex: 'lokasyonAdi',
      key: 'lokasyonAdi',
      ellipsis: true,
      width: 130,
    },
    {
      title: 'Kabin No',
      dataIndex: 'kabinNo',
      key: 'kabinNo',
      width: 80,
    },
    {
      title: 'Kira Bedeli',
      dataIndex: 'kiraBedeli',
      key: 'kiraBedeli',
      width: 80,
      render: (value) => formatMoney(value || 0),
    },
    {
      title: 'Kabin Bedeli',
      dataIndex: 'kabinBedeli',
      key: 'kabinBedeli',
      width: 80,
      render: (value) => formatMoney(value || 0),
    },
    {
      title: 'Elektrik Bedeli',
      dataIndex: 'elektrikBedeli',
      key: 'elektrikBedeli',
      width: 90,
      render: (value) => formatMoney(value || 0),
    },
    {
      title: 'Ortak Alan Gideri',
      dataIndex: 'ortakAlanBedeli',
      key: 'ortakAlanBedeli',
      width: 100,
      render: (value) => formatMoney(value || 0),
    },
    {
      title: 'Kira Başlangıç',
      dataIndex: 'kiraBaslangic',
      key: 'kiraBaslangic',
      width: 100,
      render: (value) => value ? dayjs(value).format('DD.MM.YYYY') : '-',
    },
    {
      title: 'İşlem',
      key: 'actions',
      width: 70,
      render: (_, record) => (
        <Space>
        <Button 
          type="text"
          icon={<EditOutlined />}
          size="small"
          onClick={() => handleKabinDuzenle(record.id)}
        />
        <Popconfirm
          title="Bu kabini silmek istediğinizden emin misiniz?"
          onConfirm={() => handleKabinSil(record.id)}
          okText="Evet"
          cancelText="Hayır"
        >
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />}
            size="small"
          />
        </Popconfirm>
        </Space>
      ),
    },
  ];

  const modalLokasyonKolonlari = [
    {
      title: 'Lokasyon No',
      dataIndex: 'maptriksID',
      key: 'maptriksID',
      width: 120,
    },
    {
      title: 'Lokasyon Adı',
      dataIndex: 'Name',
      key: 'Name',
      ellipsis: true,
    },
    {
      title: 'Durum',
      dataIndex: 'Durum',
      key: 'durum',
      width: 100,
    },
    {
      title: 'Kategori',
      dataIndex: 'Kategori',
      key: 'kategori',
      width: 120,
    },
    {
      title: 'İl',
      dataIndex: 'İl',
      key: 'il',
      width: 100,
    },
    {
      title: 'İlçe',
      dataIndex: 'İlçe',
      key: 'ilce',
      width: 120,
    },
    {
      title: 'Seç',
      key: 'sec',
      width: 80,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            setSelectedLokasyon(record);
            setLokasyonModalVisible(false);
          }}
        >
          Seç
        </Button>
      ),
    },
  ];

  // 27.10 düzeltmesi: submit-form Update'te de 'ekleyen'i gönderen kullanıcıyla dolduruyor
  // (form 231'de ekleyen gizli alan, şemada yok); sözleşmenin ilk kaydedeni kayboluyordu.
  // Mevcut değer extra_data ile aynen geri yazılır (null gönderilemez: submit-form None'u
  // 'None' metnine çevirir); form, bu sözleşmenin başlığı yüklenmeden açılmaz.
  const bankaBaslikHazir = !!(formData?.guncelTablo && bankaBaslik && String(bankaBaslik.id) === String(formData.uniqueValue));
  const guncelExtraData = useMemo(() => (
    bankaBaslikHazir && bankaBaslik.ekleyen !== null && bankaBaslik.ekleyen !== undefined
      ? { ...(formData?.extraData || {}), ekleyen: bankaBaslik.ekleyen }
      : (formData?.extraData || { _override_function: 'Update' })
  ), [bankaBaslikHazir, formData?.extraData, bankaBaslik]);

  // 27.10: sozlesme'de bu seçimler varchar; FormikDynamicForm '0' değerli seçimi boş gösterip
  // kaydette '' yazıyor (ör. 'Yıllık Peşin' / 'Artış Yok'). Kullanıcı yeniden seçsin diye uyarılır.
  const sifirSecimler = bankaBaslikHazir
    ? [['odemeSekli', 'Ödeme Şekli: Yıllık Peşin'], ['artisOrani', 'Artış Oranı: Artış Yok'], ['artisSekli', 'Artış Şekli: Yok']]
      .filter(([alan]) => String(bankaBaslik?.[alan] ?? '') === '0')
      .map(([, etiket]) => etiket)
    : [];

  const handleFormSuccess = (result) => {
    console.log('Form başarıyla gönderildi:', result);
    if (formData?.guncelTablo) {
      fetchBankaSatirlari();
    } else if (formData?.uniqueValue) {
      fetchKabinler();
    }
  };

  if (!formData) {
    return (
      <div style={{ 
        padding: '24px',
        minHeight: '100vh',
        background: darkMode ? 'var(--dark-bg)' : 'var(--color-bg)'
      }}>
        <Card
          style={{
            background: darkMode ? '#1b1d22' : '#fff',
            border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
          }}
        >
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <p style={{
              marginTop: 16,
              color: darkMode ? '#fff' : '#000'
            }}>
              Yükleniyor...
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '24px',
      minHeight: '100vh',
      background: darkMode ? 'var(--dark-bg)' : 'var(--color-bg)'
    }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {formData.guncelTablo && (
          <Text strong style={{ color: darkMode ? '#fff' : '#000', fontSize: 16 }}>
            Banka Sözleşmesi #{formData.uniqueValue}
            {bankaBaslik?.unvan ? ` — ${bankaBaslik.unvan}` : ''}
          </Text>
        )}
        {formData.guncelTablo && bankaBulunamadi ? (
          <Alert
            type="warning"
            showIcon
            message={`#${formData.uniqueValue} numaralı banka sözleşmesi bulunamadı`}
            description="Bağlantı bir lokasyon numarası ya da silinmiş bir sözleşme gösteriyor olabilir; form açılmadı."
          />
        ) : formData.guncelTablo && bankaBaslikHata && !bankaBaslikHazir ? (
          <Alert
            type="error"
            showIcon
            message="Sözleşme bilgisi yüklenemedi; form açılmadı"
            action={<Button size="small" onClick={fetchBankaSatirlari}>Tekrar Dene</Button>}
          />
        ) : formData.guncelTablo && !bankaBaslikHazir ? (
          <Card size="small"><div style={{ textAlign: 'center', padding: 24 }}><Spin /></div></Card>
        ) : (
          <>
            {sifirSecimler.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Kayıtlı değer formda boş görünüyor: ${sifirSecimler.join(', ')}`}
                description="Kaydetmeden önce bu alanları formda yeniden seçin; seçilmezse boş kaydedilir."
              />
            )}
            <FormikForm
              formId={formData.formId}
              tableName={formData.tableName}
              uniqueField={formData.uniqueField}
              uniqueValue={formData.uniqueValue}
              onSuccess={handleFormSuccess}
              extraData={guncelExtraData}
            />
          </>
        )}

        {/* 26.10: güncel tabloda sözleşmeye bağlı banka_sozlesme_kayit satırları; 27.10: ekle / düzenle / sil */}
        {formData.guncelTablo && !bankaBulunamadi && (
          <BskSatirlari apiBase={API_BASE_URL} sozlesmeId={formData.uniqueValue} satirlar={bankaSatirlari}
            loading={bankaSatirYukleniyor} onDegisti={fetchBankaSatirlari} darkMode={darkMode}
            acilacakId={new URLSearchParams(location.search).get('bsk')} />
        )}

        {/* Sözleşmeye Bağlı Kabinler (eski 'sozlesmeler' tablosu akışı) */}
        {formData.uniqueValue && !formData.guncelTablo && (
          <Card
            title={
              <Space>
                <Text strong style={{ color: darkMode ? '#fff' : '#000' }}>
                  Sözleşmeye Bağlı Kabinler
                </Text>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleLokasyonModalAc}
                  size="small"
                >
                  Lokasyon Ekle
                </Button>
              </Space>
            }
            style={{
              background: darkMode ? '#1b1d22' : '#fff',
              border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={16}>
                <div style={{ 
                  maxHeight: '600px', 
                  overflow: 'auto',
                  border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
                  borderRadius: '4px'
                }}>
                  <Table
                    columns={kabinKolonlari}
                    dataSource={kabinler}
                    loading={kabinlerLoading}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    scroll={{ x: 700 }}
                    className={darkMode ? 'dark-table' : 'light-table'}
                  />
                </div>
              </Col>
              
              <Col xs={24} lg={8}>
                {selectedLokasyon && (
                  <Card
                    title={`Seçilen Lokasyon: ${selectedLokasyon?.Name || selectedLokasyon?.['Lokasyon Adı'] || 'Lokasyon seçilmedi'}`}
                    size="small"
                    style={{
                      background: darkMode ? '#2d3748' : '#f5f5f5',
                      border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
                    }}
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                      <div>
                        <Text strong style={{ color: darkMode ? '#fff' : '#000' }}>
                          Kabin
                        </Text>
                        <Select
                          placeholder="Kabin seçin"
                          value={selectedKabin}
                          onChange={setSelectedKabin}
                          style={{ width: '100%', marginTop: 4 }}
                          loading={kabinLoading}
                          disabled={!selectedLokasyon || kabinLoading}
                          showSearch
                          filterOption={(input, option) =>
                            trIncludes(option?.label ?? '', input)
                          }
                        >
                          {kabinListesi.map(kabin => (
                            <Option 
                              key={kabin.value} 
                              value={kabin.value}
                              disabled={kabin.disabled}
                            >
                              {kabin.label}
                            </Option>
                          ))}
                        </Select>
                      </div>

                      <div>
                        <Text strong style={{ color: darkMode ? '#fff' : '#000' }}>
                          Kira Bedeli
                        </Text>
                        <InputNumber
                          placeholder="Kira bedeli"
                          value={kiraBedeli}
                          onChange={setKiraBedeli}
                          style={{ width: '100%', marginTop: 4 }}
                          min={0}
                          {...inputNumberMoneyProps}
                        />
                      </div>

                      <div>
                        <Text strong style={{ color: darkMode ? '#fff' : '#000' }}>
                          Kabin Bedeli
                        </Text>
                        <InputNumber
                          placeholder="Kabin bedeli"
                          value={kabinBedeli}
                          onChange={setKabinBedeli}
                          style={{ width: '100%', marginTop: 4 }}
                          min={0}
                          {...inputNumberMoneyProps}
                        />
                      </div>

                      <div>
                        <Text strong style={{ color: darkMode ? '#fff' : '#000' }}>
                          Elektrik Bedeli
                        </Text>
                        <InputNumber
                          placeholder="Elektrik bedeli"
                          value={elektrikBedeli}
                          onChange={setElektrikBedeli}
                          style={{ width: '100%', marginTop: 4 }}
                          min={0}
                          {...inputNumberMoneyProps}
                        />
                      </div>

                      <div>
                        <Text strong style={{ color: darkMode ? '#fff' : '#000' }}>
                          Ortak Alan Gideri
                        </Text>
                        <InputNumber
                          placeholder="Ortak alan gideri"
                          value={ortakAlanBedeli}
                          onChange={setOrtakAlanBedeli}
                          style={{ width: '100%', marginTop: 4 }}
                          min={0}
                          {...inputNumberMoneyProps}
                        />
                      </div>

                      <div>
                        <Text strong style={{ color: darkMode ? '#fff' : '#000' }}>
                          Kira Başlangıç Tarihi
                        </Text>
                        <DatePicker
                          placeholder="Tarih seçin"
                          value={kiraBaslangic}
                          onChange={setKiraBaslangic}
                          style={{ width: '100%', marginTop: 4 }}
                          format="DD.MM.YYYY"
                        />
                      </div>

                      <Space>
                        <Button
                          type="primary"
                          onClick={handleKabinEkle}
                          loading={eklemeLoading}
                          disabled={!selectedKabin}
                        >
                          Ekle
                        </Button>
                        <Button onClick={resetForm}>
                          İptal
                        </Button>
                      </Space>
                    </Space>
                  </Card>
                )}
              </Col>
            </Row>
          </Card>
        )}

        {/* Lokasyon Seçim Modal */}
        <Modal
          title="Lokasyon Seçiniz"
          open={lokasyonModalVisible}
          onCancel={() => {
            setLokasyonModalVisible(false);
            resetForm();
          }}
          width={1000}
          footer={null}
          className={darkMode ? 'dark-modal' : 'light-modal'}
        >
          <div style={{ marginBottom: 16 }}>
            <Input
              placeholder="Lokasyon ara... (ID, Ad, Durum, Kategori, İl, İlçe)"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: '100%' }}
              prefix={<SearchOutlined />}
            />
          </div>
          <Table
            columns={modalLokasyonKolonlari}
            dataSource={filteredLokasyonListesi}
            loading={lokasyonLoading}
            rowKey="maptriksID"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} lokasyon`
            }}
            className={darkMode ? 'dark-table' : 'light-table'}
            scroll={{ x: 800 }}
          />
        </Modal>

        {/* Form Modal */}
        {formModalOpen && (
          <Modal
            title={modalTitle}
            open={formModalOpen}
            onCancel={closeFormModal}
            footer={null}
            destroyOnHidden={false}
            width={800}
            styles={{
              body: {
                background: darkMode ? '#1b1d22' : '#fff',
                padding: '16px'
              }
            }}
          >
            <div style={{ 
              maxHeight: 600, 
              overflowY: 'auto',
              background: darkMode ? '#1b1d22' : '#fff'
            }}>
              <FormikForm
                formId={currentFormId}
                uniqueValue={currentUniqueValue}
                uniqueField={currentUniqueField}
                tableName={currentTableName}
                extraData={formExtraData}
                onSuccess={handleFormModalSuccess}
                onCancel={closeFormModal}
              />
            </div>
          </Modal>
        )}
      </Space>
    </div>
  );
}

export default BankaSozlesmeFormDetay;